import React, { useEffect, useState } from "react";
import { biGet, errText } from "./biApi";
import { Card, Tabs, Empty, Loading } from "./BiUi";
import { TIMEFRAMES, STATUS_EMOJI, dateRangeLabel, downloadStaffCompliancePDF, shareOnWhatsApp } from "./complianceReport";

const sortByClass = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });
const TONE = {
  Exemplary: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Needs Improvement": "bg-amber-50 text-amber-700 border-amber-200",
  "Action Required": "bg-rose-50 text-rose-700 border-rose-200",
  Holiday: "bg-slate-100 text-slate-500 border-slate-200"
};
const flag = (v) => (v === "Pending" ? "text-rose-600" : v === "Late" ? "text-amber-600" : "text-emerald-600");
const pctCls = (v) => (v >= 90 ? "text-emerald-600" : v >= 70 ? "text-amber-600" : "text-rose-600");

const DailyCells = ({ r, att, diary }) => {
  const cell = (status, time) => (r.holiday ? "Holiday" : status === "Pending" ? "⏳ Pending" : `✓ ${time || "Submitted"}`);
  return (
    <>
      {att && <td className={`pr-3 font-bold ${flag(r.attendanceStatus)}`}>{cell(r.attendanceStatus, r.attendanceTime)}</td>}
      {diary && <td className={`pr-3 font-bold ${flag(r.diaryStatus)}`}>{cell(r.diaryStatus, r.diaryTime)}</td>}
      {diary && !att && <td className="pr-3 font-bold text-slate-700">{r.holiday ? "—" : r.topicsLogged ?? 0}</td>}
      {att && <td className={`pr-3 font-bold ${flag(r.punctuality)}`}>{r.punctuality}</td>}
    </>
  );
};

const PeriodCells = ({ r, att, diary }) => {
  const pc = (v) => <td className={`pr-3 font-bold ${r.holiday ? "text-slate-400" : pctCls(v)}`}>{r.holiday ? "—" : `${v}%`}</td>;
  const days = att && !diary ? r.attendanceDays : diary && !att ? r.diaryDays : r.completedDays;
  return (
    <>
      <td className="pr-3 font-bold text-slate-700">{r.holiday ? "—" : `${days}/${r.workingDays}`}</td>
      {att && pc(r.attendanceRate)}{diary && pc(r.diaryRate)}{att && pc(r.punctualityRate)}
    </>
  );
};

const Check = ({ checked, onChange, label }) => (
  <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-black text-slate-700 shadow-sm">
    <input type="checkbox" className="h-3.5 w-3.5 accent-slate-900" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

/** Staff Compliance roster: Daily / Weekly / Monthly toggle + PDF export + WhatsApp share. */
export default function StaffComplianceReport({ user, activeOnly, classId = "", reload = 0 }) {
  const [tf, setTf] = useState("daily");
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [includeAttendance, setIncludeAttendance] = useState(true);
  const [includeDiary, setIncludeDiary] = useState(false);
  // Export scope: if neither is ticked, fall back to the combined report
  const scope = { includeAttendance: includeAttendance || !includeDiary, includeDiary: includeDiary || !includeAttendance };
  const showAtt = scope.includeAttendance, showDiary = scope.includeDiary;

  useEffect(() => {
    setReport(null); setErr("");
    biGet(user, "/api/analytics/staff-audit", { timeframe: tf, activeOnly, ...(classId ? { classId } : {}) })
      .then((r) => (r.error ? setErr(r.error) : setReport(r)))
      .catch((e) => setErr(errText(e)));
  }, [user, tf, activeOnly, classId, reload]);

  const rows = report ? [...report.rows].sort((a, b) => sortByClass(a.classId, b.classId)) : [];
  const daily = tf === "daily";
  const share = async () => {
    const copied = await shareOnWhatsApp(report, scope);
    setNote(copied ? "Summary copied to clipboard & WhatsApp opened" : "WhatsApp opened");
    setTimeout(() => setNote(""), 4000);
  };
  const btn = "rounded-xl px-4 py-2 text-xs font-black text-white shadow-sm disabled:opacity-50";

  return (
    <Card title="Staff Compliance & Teacher Audit" className="lg:col-span-2"
      subtitle={report ? `${report.label} • ${dateRangeLabel(report)} • ${report.workingDays} working day(s)` : "Attendance, diary & punctuality per teacher"}
      right={
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={tf} onChange={setTf} options={TIMEFRAMES} />
          <Check checked={includeAttendance} onChange={setIncludeAttendance} label="Attendance Audit" />
          <Check checked={includeDiary} onChange={setIncludeDiary} label="Daily Diary Audit" />
        </div>
      }>
      {err && <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700">{err}</div>}
      {!report && !err && <Loading />}
      {report && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => downloadStaffCompliancePDF(report, scope)} disabled={!rows.length} className={`${btn} bg-slate-900 hover:bg-slate-700`}>
              📄 Download Staff Compliance PDF
            </button>
            <button type="button" onClick={share} disabled={!rows.length} className={`${btn} bg-emerald-600 hover:bg-emerald-700`}>
              📲 Share Compliance on WhatsApp
            </button>
            {note && <span className="text-xs font-bold text-emerald-700">✓ {note}</span>}
            <span className="ml-auto text-xs font-bold text-slate-500">
              Full compliance: {report.summary.compliant}/{report.summary.totalActiveClasses} • Pending: {report.summary.pending}
            </span>
          </div>
          {!rows.length ? <Empty /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Class</th><th className="pr-3">Teacher</th>
                    {daily
                      ? <>
                          {showAtt && <th className="pr-3">Attendance</th>}
                          {showDiary && <th className="pr-3">Diary</th>}
                          {showDiary && !showAtt && <th className="pr-3">Topics Logged</th>}
                          {showAtt && <th className="pr-3">Punctuality</th>}
                        </>
                      : <>
                          <th className="pr-3">Days Completed</th>
                          {showAtt && <th className="pr-3">Attendance %</th>}
                          {showDiary && <th className="pr-3">Diary %</th>}
                          {showAtt && <th className="pr-3">On-Time %</th>}
                        </>}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.classId} className="hover:bg-slate-50">
                      <td className="py-2.5 pr-3 font-black text-slate-900">{r.classId}</td>
                      <td className="pr-3 font-semibold text-slate-700">{r.teacherName}</td>
                      {daily ? <DailyCells r={r} att={showAtt} diary={showDiary} /> : <PeriodCells r={r} att={showAtt} diary={showDiary} />}
                      <td><span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${TONE[r.status]}`}>{STATUS_EMOJI[r.status]} {r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
